/*******************************************************************************
 * Copyright 2026 Collaterate. All rights reserved.
 *******************************************************************************/

// TTI-20177 backlog#1: shared REST-mode admin upload helper (presigned direct-to-S3).
//
// Replaces the legacy plupload/uploadify byte-POST-to-app-server flows for screens running in
// REST mode (restUiEnabled): instead of streaming file bytes through pub/handler classes into
// HTTP-session staging, the browser
//   1. POSTs /stagedFileReservations/create to reserve a staged-file slot (server returns the
//      unique reservation name plus a signed S3 upload configuration), then
//   2. sends the file bytes DIRECTLY to S3 using that signed configuration, then
//   3. hands the reservation name (stagedFileReservationName) to a consuming REST endpoint,
//      which validates/marks the reservation filled and runs the same backend save the
//      session-staged File used to feed (see StagedFileWorkingResourceProvider server-side).
//
// Step 2 uses the signed browser-POST configuration (s3PostConfig: policy + v4 signature form
// fields, success_action_status=201) rather than the also-returned presigned PUT url (s3Url):
// the form-POST flavor is the one production S3 buckets already accept cross-origin from admin
// pages — it is exactly what the admin Order Detail uploader (S3FileUploaderDefaultSettings in
// orderDetailController.js) and the storefront plupload runtimes have always sent — so no bucket
// CORS changes are required for this rework.
//
// Plain ES5 + jQuery, same conventions as restComet.js. All functions return jQuery promises.
// Failures are rejected with an ARRAY of user-displayable message strings.
var restUpload = (function () {
    // Matches the reservation-create payload the admin Order Detail uploader already sends;
    // S3_UPLOAD_HTML5 is the SubmissionProfile whose policy allows the custom form fields below.
    var SUBMISSION_PROFILE = 'S3_UPLOAD_HTML5';

    function rejectMessagesFromXhr (jqXHR, fallbackMessage) {
        if (typeof restComet !== 'undefined' && jqXHR && jqXHR.responseJSON) {
            return restComet.errorsFromResponse(jqXHR);
        }
        return [fallbackMessage];
    }

    return {

        // Step 1: reserve a staged-file slot. Resolves with the StagedFileReservationResource
        // ({name, displayName, s3PostConfig: {...}}). `name` is the unique, timestamped
        // reservation name consuming endpoints take as stagedFileReservationName.
        createReservation: function (displayName) {
            var deferred = $.Deferred();
            $.ajax({
                url: REST_ROOT_URL + '/stagedFileReservations/create',
                type: 'POST',
                contentType: 'application/json',
                data: JSON.stringify({
                    displayName: displayName,
                    submissionProfile: SUBMISSION_PROFILE
                }),
                dataType: 'json',
                cache: false
            }).done(function (reservation) {
                deferred.resolve(reservation);
            }).fail(function (jqXHR) {
                deferred.reject(rejectMessagesFromXhr(jqXHR, 'Could not prepare the file upload. Please try again later.'));
            });
            return deferred.promise();
        },

        // Step 2: send the file bytes directly to S3 with the reservation's signed form-POST
        // configuration. Same form fields plupload sends via S3FileUploaderDefaultSettings.
        // opts.onProgress(percentInt) is invoked as the browser reports upload progress.
        uploadToS3: function (reservation, file, opts) {
            var options = opts || {};
            var deferred = $.Deferred();
            var config = reservation.s3PostConfig;
            var formData = new FormData();
            formData.append('key', config.key);
            formData.append('Filename', config.displayName);
            formData.append('acl', config.accessControlList);
            formData.append('success_action_status', '201');
            formData.append('AWSAccessKeyId', config.awsAccessKeyId);
            formData.append('policy', config.policy);
            formData.append('x-amz-algorithm', config.amzAlgorithm);
            formData.append('x-amz-credential', config.amzCredential);
            formData.append('x-amz-date', config.amzDate);
            formData.append('x-amz-signature', config.v4Signature);
            formData.append('signature', config.v2Signature);

            // The S3 policy requires a "name" field (SubmissionProfile.S3_UPLOAD_HTML5); without it S3 answers 403.
            formData.append('name', config.fileName);

            // TTI-20991: typed image uploads (e.g. OfferingImage) sign the content type, so it is sent with the file.
            if (options.contentType) {
                formData.append('Content-Type', options.contentType);
            }

            // Metadata the server signed into the policy (e.g. the uploader's id on an OfferingImage) must be posted back as
            // x-amz-meta-* fields, or S3 rejects the upload.
            var meta = reservation.meta || {};
            Object.keys(meta).forEach(function (metaKey) {
                formData.append('x-amz-meta-' + metaKey, meta[metaKey]);
            });

            // S3 requires the file part last, named "file"
            formData.append('file', file);
            $.ajax({
                url: config.bucketUrl,
                type: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                xhr: function () {
                    var xhr = $.ajaxSettings.xhr();
                    if (xhr.upload && typeof options.onProgress === 'function') {
                        xhr.upload.addEventListener('progress', function (event) {
                            if (event.lengthComputable && event.total > 0) {
                                options.onProgress(Math.round((event.loaded / event.total) * 100));
                            }
                        });
                    }
                    return xhr;
                }
            }).done(function () {
                deferred.resolve(reservation);
            }).fail(function () {
                deferred.reject(['The file could not be uploaded. Please try again later.']);
            });
            return deferred.promise();
        },

        // Steps 1+2 composed: reserve, then upload. Resolves with the reservation resource;
        // pass reservation.name to the consuming endpoint as stagedFileReservationName.
        //   opts.onProgress(percentInt) — S3 transfer progress.
        uploadFile: function (file, opts) {
            var options = opts || {};
            var deferred = $.Deferred();
            restUpload.createReservation(file.name)
                .done(function (reservation) {
                    restUpload.uploadToS3(reservation, file, options)
                        .done(function () {
                            deferred.resolve(reservation);
                        })
                        .fail(function (messages) {
                            deferred.reject(messages);
                        });
                })
                .fail(function (messages) {
                    deferred.reject(messages);
                });
            return deferred.promise();
        },

        // TTI-20991: signed browser-POST configuration for a typed S3 location (e.g. 'OfferingImage', stored at
        // resources/offering_images/{displayNameWithDateTime}). Resolves with the S3FileUploadConfigurationDTO
        // ({displayNameWithDateTime, s3PostConfig: {...}}), which uploadToS3 accepts in place of a reservation.
        //   siteId — the site of the product being edited (site-owned or site-share), or null for a master
        //            product; used for the access check only, never added to an offering image key.
        createS3UploadConfig: function (file, configurationType, siteId) {
            var deferred = $.Deferred();
            $.ajax({
                url: REST_ROOT_URL + '/s3FileUploadConfiguration/v2/create',
                type: 'POST',
                contentType: 'application/json',
                data: JSON.stringify({
                    name: file.name,
                    displayName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    configurationType: configurationType,
                    siteId: siteId || null,
                    submissionProfile: SUBMISSION_PROFILE
                }),
                dataType: 'json',
                cache: false
            }).done(function (response) {
                deferred.resolve(response.content);
            }).fail(function (jqXHR) {
                deferred.reject(rejectMessagesFromXhr(jqXHR, 'Could not prepare the file upload. Please try again later.'));
            });
            return deferred.promise();
        },

        // TTI-20991: the natural width and height of an image file, read in the browser (the server no longer sees
        // the bytes). Always resolves; a size that cannot be read (e.g. an SVG with only a viewBox) is null.
        readImageSize: function (file) {
            var deferred = $.Deferred();
            var url;
            try {
                url = URL.createObjectURL(file);
            } catch (e) {
                return deferred.resolve({ width: null, height: null }).promise();
            }
            var image = new Image();
            image.onload = function () {
                URL.revokeObjectURL(url);
                deferred.resolve({ width: image.naturalWidth || null, height: image.naturalHeight || null });
            };
            image.onerror = function () {
                URL.revokeObjectURL(url);
                deferred.resolve({ width: null, height: null });
            };
            image.src = url;
            return deferred.promise();
        },

        // TTI-20991: uploads a product image or thumbnail straight to S3 at resources/offering_images/{fileName}
        // (flat, the same path as its /resources/offering_images/{fileName} URL). Resolves with
        // {fileName, width, height}: fileName is the timestamped name to save on the product.
        //   opts.siteId — see createS3UploadConfig; opts.onProgress(percentInt) — S3 transfer progress.
        uploadOfferingImage: function (file, opts) {
            var options = opts || {};
            var deferred = $.Deferred();
            restUpload.readImageSize(file).done(function (size) {
                restUpload.createS3UploadConfig(file, 'OfferingImage', options.siteId)
                    .done(function (config) {
                        restUpload.uploadToS3(config, file, {
                            contentType: file.type || 'application/octet-stream',
                            onProgress: options.onProgress
                        }).done(function () {
                            deferred.resolve({ fileName: config.displayNameWithDateTime, width: size.width, height: size.height });
                        }).fail(function (messages) {
                            deferred.reject(messages);
                        });
                    })
                    .fail(function (messages) {
                        deferred.reject(messages);
                    });
            });
            return deferred.promise();
        },

        // True when fileName's extension is in allowedExtensions (array of lowercase strings,
        // no dots). Mirrors the client-side filters the plupload configs enforced.
        hasAllowedExtension: function (fileName, allowedExtensions) {
            if (!allowedExtensions || allowedExtensions.length === 0) {
                return true;
            }
            var dot = String(fileName).lastIndexOf('.');
            if (dot < 0) {
                return false;
            }
            var extension = String(fileName).substring(dot + 1).toLowerCase();
            for (var i = 0; i < allowedExtensions.length; i++) {
                if (allowedExtensions[i] === extension) {
                    return true;
                }
            }
            return false;
        },

        // Opens a native file picker (replacement for the plupload browse-button wiring) and
        // invokes callback(file) with the chosen File. A fresh transient <input type=file> is
        // used per call, so repeat picks of the same file still fire.
        //   opts.accept — optional accept attribute value (e.g. 'image/*' or '.wsdl,.xml').
        pickFile: function (opts, callback) {
            var options = opts || {};
            var $input = $('<input type="file" style="display:none">');
            if (options.accept) {
                $input.attr('accept', options.accept);
            }
            $input.on('change', function () {
                var file = this.files && this.files[0];
                $input.remove();
                if (file) {
                    callback(file);
                }
            });
            $('body').append($input);
            $input.trigger('click');
        }
    };
}());
